import { Label } from '@ophelos/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@ophelos/ui/components/radio-group';
import { useTranslation } from '@/hooks/useTranslation';
import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { paymentMethods } from './payment-method-constants';

interface PaymentMethodSelectionSimpleProps {
  selectedMethod: PaymentMethod | null;
  onSelect: (method: PaymentMethod) => void;
  disabled?: boolean;
  availableMethods?: PaymentMethod[];
  /** Methods present in the list but not yet selectable — shown greyed out
   *  with the given reason instead of being hidden (e.g. a lead-time floor
   *  the picked date hasn't cleared). */
  disabledReasons?: Partial<Record<PaymentMethod, string>>;
}

export function PaymentMethodSelectionSimple({
  selectedMethod,
  onSelect,
  disabled,
  availableMethods,
  disabledReasons,
}: PaymentMethodSelectionSimpleProps) {
  const { t } = useTranslation();
  const { data: availability, isLoading } = usePaymentMethodAvailability();

  const isDisabled = disabled || isLoading;

  const methods = paymentMethods
    .filter((m) => availability?.[m.value] ?? true)
    .filter((m) => !availableMethods || availableMethods.includes(m.value));

  return (
    <RadioGroup
      value={selectedMethod ?? ''}
      onValueChange={(value) => onSelect(value as PaymentMethod)}
      disabled={isDisabled}
      className="gap-0 w-full rounded-lg border bg-card divide-y divide-border overflow-hidden"
    >
      {methods.map((method) => {
        const Icon = method.icon;
        const id = `payment-method-${method.value}`;
        const reason = disabledReasons?.[method.value];
        const rowDisabled = isDisabled || !!reason;
        const isSelected = selectedMethod === method.value;

        return (
          <Label
            key={method.value}
            htmlFor={id}
            className={`overflow-hidden flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
              rowDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-soft'
            } ${isSelected ? 'bg-linear-to-l from-accent/40' : ''}`}
          >
            <RadioGroupItem id={id} value={method.value} disabled={rowDisabled} className="size-5 [&_svg]:size-3" />
            <span className="flex-1 stack text-base">
              {t(method.labelKey)}
              <span className="text-xs font-normal text-muted-foreground">{reason ?? t(method.descriptionKey)}</span>
            </span>
            <span
              className={`flex items-center justify-center size-8 shrink-0 transition-all duration-300 ${
                isSelected ? 'grayscale-0 opacity-70 -rotate-5' : 'grayscale opacity-25'
              }`}
            >
              <span className="p-2.5 border border-ring/8 rounded-[1.5rem] bg-accent/20">
                <span className="block p-2 border border-ring/15 rounded-[1rem] shadow-md shadow-ring/10 bg-accent/35">
                  <span className="block p-1.25 border border-ring/30 rounded-[0.5rem] shadow-md shadow-ring/20 bg-card/75">
                    <Icon className="size-4 text-accent-foreground" />
                  </span>
                </span>
              </span>
            </span>
          </Label>
        );
      })}
    </RadioGroup>
  );
}
